import {useMemo} from 'react';

import {
  CategorizedValidationErrors,
  SetupWizardMutationPayload,
  SetupWizardStateView,
  getStateViewDefaults,
} from 'src/lib/api/setupWizard';
import {ARE_OBSERVATIONS_AVAILABLE_COLUMN} from 'src/lib/setupWizardConstants';

export type StepStatusKind = 'not_started' | 'in_progress' | 'complete' | 'warning' | 'error';

export interface StepStatus {
  status: StepStatusKind;
  summary: string;
  errors: string[];
  warnings: string[];
  prerequisiteWarning: string | null;
}

export interface AllStepStatuses {
  projectSettings: StepStatus;
  defineSites: StepStatus;
  runDelineation: StepStatus;
  reviewApply: StepStatus;
  observations: StepStatus;
  saveExport: StepStatus;
}

const EMPTY_ERRORS: CategorizedValidationErrors = {sites: [], observations: []};

// --- Step status computation ---

function computeProjectSettings(view: ReturnType<typeof getStateViewDefaults>): StepStatus {
  const hasTimezone = Boolean(view.timeZone);
  return {
    status: hasTimezone ? 'complete' : 'not_started',
    summary: hasTimezone ? `${view.units} · ${view.timeZone}` : 'Units and timezone not set',
    errors: [],
    warnings: [],
    prerequisiteWarning: null,
  };
}

function computeDefineSites(
  view: ReturnType<typeof getStateViewDefaults>,
  errors: string[]
): StepStatus {
  const siteCount = view.siteInfo.length;
  const projectSettingsComplete = Boolean(view.timeZone);

  const prerequisiteWarning = !projectSettingsComplete
    ? 'Set units and timezone in Project Settings first.'
    : null;

  if (siteCount === 0) {
    return {
      status: 'not_started',
      summary: 'No sites defined',
      errors,
      warnings: [],
      prerequisiteWarning,
    };
  }

  if (errors.length > 0) {
    return {
      status: 'error',
      summary: `${siteCount} site${siteCount !== 1 ? 's' : ''} · ${errors.length} issue${errors.length !== 1 ? 's' : ''}`,
      errors,
      warnings: [],
      prerequisiteWarning,
    };
  }

  return {
    status: 'complete',
    summary: `${siteCount} site${siteCount !== 1 ? 's' : ''}`,
    errors: [],
    warnings: [],
    prerequisiteWarning,
  };
}

function computeRunDelineation(view: ReturnType<typeof getStateViewDefaults>): StepStatus {
  const hasSites = view.siteInfo.length > 0;
  const prerequisiteWarning = !hasSites
    ? 'Define at least one site in Define Sites before running delineation.'
    : null;

  return {
    status: view.hasDelineation ? 'complete' : 'not_started',
    summary: view.hasDelineation ? 'Complete' : 'Not run',
    errors: [],
    warnings: [],
    prerequisiteWarning,
  };
}

function computeReviewApply(
  view: ReturnType<typeof getStateViewDefaults>,
  _state: SetupWizardStateView
): StepStatus {
  if (!view.hasDelineation) {
    return {
      status: 'not_started',
      summary: 'Run delineation first',
      errors: [],
      warnings: [],
      prerequisiteWarning: 'Run delineation in Run Delineation first.',
    };
  }

  return {
    status: 'complete',
    summary: `${view.siteInfo.length} site${view.siteInfo.length !== 1 ? 's' : ''}`,
    errors: [],
    warnings: [],
    prerequisiteWarning: null,
  };
}

function computeObservations(
  view: ReturnType<typeof getStateViewDefaults>,
  errors: string[]
): StepStatus {
  const hasSites = view.siteInfo.length > 0;
  const prerequisiteWarning = !hasSites
    ? 'Define sites and run delineation before configuring observations.'
    : null;

  const sitesNeedingObs = view.siteInfo.filter(
    (s) => s[ARE_OBSERVATIONS_AVAILABLE_COLUMN] === true
  ).length;
  const sitesWithObs = view.uploadedObsSummary.filter((s) => s.hasData).length;

  const summary =
    sitesNeedingObs === 0
      ? 'No sites need observations'
      : `${sitesWithObs} / ${sitesNeedingObs} sites have data`;

  const status: StepStatusKind =
    errors.length > 0
      ? 'error'
      : sitesNeedingObs === 0 || sitesWithObs === 0
        ? 'not_started'
        : sitesWithObs < sitesNeedingObs
          ? 'in_progress'
          : 'complete';

  return {status, summary, errors, warnings: [], prerequisiteWarning};
}

function computeSaveExport(
  view: ReturnType<typeof getStateViewDefaults>,
  state: SetupWizardStateView
): StepStatus {
  const hasSites = view.siteInfo.length > 0;
  const prerequisiteWarning = !view.hasDelineation
    ? 'Run delineation before committing to the database.'
    : null;

  if (!hasSites) {
    return {
      status: 'not_started',
      summary: 'No sites defined',
      errors: [],
      warnings: [],
      prerequisiteWarning: prerequisiteWarning ?? 'Define sites first.',
    };
  }

  if (state.committedFcid != null) {
    return {
      status: 'complete',
      summary: `Committed (Feature Collection ID: ${state.committedFcid})`,
      errors: [],
      warnings: [],
      prerequisiteWarning: null,
    };
  }

  return {
    status: 'in_progress',
    summary: 'Ready to commit',
    errors: [],
    warnings: [],
    prerequisiteWarning,
  };
}

export function useStepStatus(payload: SetupWizardMutationPayload | undefined): AllStepStatuses {
  return useMemo(() => {
    const view = getStateViewDefaults(payload?.data);
    const state = payload?.data ?? ({} as SetupWizardStateView);
    const raw = payload?.validationErrors;
    const errors: CategorizedValidationErrors = raw && !Array.isArray(raw) ? raw : EMPTY_ERRORS;

    const projectSettings = computeProjectSettings(view);
    const defineSites = computeDefineSites(view, errors.sites);
    const runDelineation = computeRunDelineation(view);
    const reviewApply = computeReviewApply(view, state);
    const observations = computeObservations(view, errors.observations);
    const saveExport = computeSaveExport(view, state);

    return {projectSettings, defineSites, runDelineation, reviewApply, observations, saveExport};
  }, [payload]);
}
